import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Alert, Box, Button, Typography } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import UndoIcon from "@mui/icons-material/Undo";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import CustomModal from "../../components/ui/modal/modal.component";
import { errorMessageOf, useShipments } from "../../api/shipping";
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../consts";
import { useHasPermission } from "../../contexts/auth";
import { Shipment } from "../../types/shipping";
import { Action } from "../../types/toolbar";

export const ShipmentDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { getShipment, cancelShipment, refreshTracking, returnToStock, downloadLabel, loading } = useShipments();
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [cancelOpened, setCancelOpened] = useState(false);

  const canView = useHasPermission("shipping.view_shipment");
  const canCancel = useHasPermission("shipping.cancel_shipment");
  const canReturn = useHasPermission("shipping.change_shipment");

  const load = useCallback(async () => {
    if (!id) return;
    const { data, success } = await getShipment(id);
    if (success) setShipment(data);
  }, [id, getShipment]);

  useEffect(() => {
    load();
    const events = ["shipmentUpdated", "shipmentStatusChanged"];
    events.forEach((event) => window.addEventListener(event, load));
    return () => events.forEach((event) => window.removeEventListener(event, load));
  }, [load]);

  const changed = (data: Shipment) => {
    setShipment(data);
    window.dispatchEvent(new Event("shipmentUpdated"));
  };

  const handleLabel = async () => {
    if (!shipment) return;
    const { success, errorData } = await downloadLabel(shipment.id, shipment.waybill);
    if (!success) {
      const body = errorData instanceof Blob ? JSON.parse(await errorData.text().catch(() => "{}")) : errorData;
      enqueueSnackbar(errorMessageOf(body, t("Could not download the label.")), ERROR_SNACKBAR);
    }
  };

  const handleRefresh = async () => {
    if (!shipment) return;
    const { data, success, errorData } = await refreshTracking(shipment.id);
    if (success) {
      changed(data);
      enqueueSnackbar(t("Tracking refreshed."), SUCCESS_SNACKBAR);
    } else {
      enqueueSnackbar(errorMessageOf(errorData, t("Could not refresh tracking.")), ERROR_SNACKBAR);
    }
  };

  const handleCancel = async () => {
    if (!shipment) return;
    const { data, success, errorData } = await cancelShipment(shipment.id);
    if (success) {
      setCancelOpened(false);
      changed(data);
      enqueueSnackbar(t("Cancellation sent to Furgonetka."), SUCCESS_SNACKBAR);
    } else {
      enqueueSnackbar(errorMessageOf(errorData, t("Could not cancel the shipment.")), ERROR_SNACKBAR);
    }
  };

  const handleReturn = async () => {
    if (!shipment) return;
    const { data, success, errorData } = await returnToStock(shipment.id);
    if (success) {
      changed(data);
      window.dispatchEvent(new Event("partUpdated"));
      enqueueSnackbar(t("The parts are back in stock."), SUCCESS_SNACKBAR);
    } else {
      // A "Problem" shipment may turn out ordered after all - the backend has
      // recorded its waybill, so show it.
      window.dispatchEvent(new Event("shipmentUpdated"));
      enqueueSnackbar(errorMessageOf(errorData, t("Could not return the parts to stock.")), ERROR_SNACKBAR);
    }
  };

  if (!canView || !shipment) {
    return <Toolbar actions={[]} />;
  }

  const actions: Action[] = [
    {
      id: "shipment-label",
      icon: <PictureAsPdfIcon />,
      label: t("Download label"),
      onClick: handleLabel,
      disabled: !shipment.waybill || loading,
    },
    {
      id: "shipment-refresh-tracking",
      icon: <RefreshIcon />,
      label: t("Refresh tracking"),
      onClick: handleRefresh,
      disabled: !["OR", "TR", "PR"].includes(shipment.status) || loading,
    },
    ...(canCancel
      ? [
          {
            id: "shipment-cancel",
            icon: <CancelIcon />,
            label: t("Cancel shipment"),
            onClick: () => setCancelOpened(true),
            color: "var(--errorColor)",
            disabled: !shipment.can_cancel || loading,
          },
        ]
      : []),
    ...(canReturn && shipment.can_return_to_stock
      ? [
          {
            id: "shipment-return-to-stock",
            icon: <UndoIcon />,
            label: t("Return parts to stock"),
            onClick: handleReturn,
            disabled: loading,
          },
        ]
      : []),
  ];

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={cancelOpened}
        setOpened={setCancelOpened}
        title={t("Cancel shipment")}
        minHeight={0}
        content={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2">
              {t("Furgonetka will call off the courier. Once it confirms, the parts go back to stock.")}
            </Typography>
            <Alert severity="warning">
              {t("Some carriers (e.g. GLS) charge for a cancelled shipment.")}
            </Alert>
            <Button variant="contained" color="error" onClick={handleCancel} disabled={loading}>
              {loading ? t("Processing...") : t("Cancel shipment")}
            </Button>
          </Box>
        }
      />
    </>
  );
};
